<script>
import { useAlert } from 'dashboard/composables';
import { downloadBlob } from '../helpers';
import {
  apiErrorCode,
  apiErrorMessage,
} from '../../../../api/realEstate/errors';
import {
  startImport,
  getImportJob,
  getImportTemplate,
} from '../../../../api/realEstate/importJobs';
import ImportJobResults from '../components/ImportJobResults.vue';
import ImportHistory from '../components/ImportHistory.vue';

const POLL_INTERVAL_MS = 2000;
const MAX_SIZE_BYTES = 10 * 1024 * 1024;
const ACTIVE_STATUSES = ['pending', 'queued', 'processing'];
const HELP_KEYS = [
  'HELP_SHEETS',
  'HELP_KEYS',
  'HELP_REQUIRED',
  'HELP_MODES',
  'HELP_SIZES',
  'HELP_LINKS',
  'HELP_LIMITS',
];
const MODES = ['merge', 'replace'];

// Upload Projects: one .xlsx workbook (docs/real-estate/be/12-import.md),
// validated and applied server-side as a background job. The Upload tab
// sends the file and polls the job; the History tab lists every past
// import. Imports of an account run one at a time, so a fresh upload can
// sit in "queued" behind an earlier one — closing the modal never cancels
// it, and History picks it up again.
export default {
  name: 'BulkImportModal',
  components: { ImportJobResults, ImportHistory },
  data() {
    return {
      helpKeys: HELP_KEYS,
      modes: MODES,
      tab: 'upload',
      file: null,
      mode: 'merge',
      // Replace deletes data, so Start asks once more before sending it.
      isConfirmingReplace: false,
      fileError: '',
      invalidTemplate: [],
      isHelpOpen: false,
      isSubmitting: false,
      isDownloadingTemplate: false,
      job: null,
      pollTimer: null,
    };
  },
  computed: {
    isRunning() {
      return !!this.job && ACTIVE_STATUSES.includes(this.job.status);
    },
    isQueued() {
      return !!this.job && ['pending', 'queued'].includes(this.job.status);
    },
    isDone() {
      return !!this.job && !this.isRunning;
    },
    progress() {
      return (this.job && this.job.progress) || {};
    },
  },
  watch: {
    mode() {
      this.isConfirmingReplace = false;
    },
    file() {
      this.isConfirmingReplace = false;
    },
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    async downloadTemplate() {
      this.isDownloadingTemplate = true;
      try {
        const { data } = await getImportTemplate();
        downloadBlob('real_estate_import_template.xlsx', data);
      } catch (err) {
        useAlert(this.$t('REAL_ESTATE.BULK_IMPORT.ERROR_TEMPLATE'));
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isDownloadingTemplate = false;
      }
    },
    // Checked here too (the server enforces the same) so a wrong file is
    // caught before it's uploaded — by extension, since browsers often
    // leave File.type empty for spreadsheets.
    handleFileChange(event) {
      const file = event.target.files[0] || null;
      event.target.value = '';
      if (!file) return;
      this.invalidTemplate = [];
      if (!/\.xlsx$/i.test(file.name)) {
        this.file = null;
        this.fileError = this.$t('REAL_ESTATE.BULK_IMPORT.ERROR_WRONG_TYPE');
      } else if (file.size > MAX_SIZE_BYTES) {
        this.file = null;
        this.fileError = this.$t('REAL_ESTATE.BULK_IMPORT.ERROR_TOO_LARGE');
      } else {
        this.file = file;
        this.fileError = '';
      }
    },
    uploadErrorMessage(err) {
      const code = apiErrorCode(err);
      if (code === 'unsupported_file_type') {
        return this.$t('REAL_ESTATE.BULK_IMPORT.ERROR_WRONG_TYPE');
      }
      if (code === 'file_too_large') {
        return this.$t('REAL_ESTATE.BULK_IMPORT.ERROR_TOO_LARGE');
      }
      return apiErrorMessage(this, err, 'REAL_ESTATE.BULK_IMPORT.ERROR_START');
    },
    // 422 invalid_template names the missing/duplicated key columns per
    // sheet: `details: [{ sheet, missing: [], duplicate: [] }]`.
    templateProblems(err) {
      const data = (err && err.response && err.response.data) || {};
      const detail = data.detail || {};
      const details = detail.details || data.details || [];
      const lines = [];
      details.forEach(item => {
        const sheet = this.sheetLabel(item.sheet);
        if (item.missing && item.missing.length) {
          lines.push(
            this.$t('REAL_ESTATE.BULK_IMPORT.INVALID_TEMPLATE_MISSING', {
              sheet,
              columns: item.missing.join(', '),
            })
          );
        }
        if (item.duplicate && item.duplicate.length) {
          lines.push(
            this.$t('REAL_ESTATE.BULK_IMPORT.INVALID_TEMPLATE_DUPLICATE', {
              sheet,
              columns: item.duplicate.join(', '),
            })
          );
        }
      });
      return lines;
    },
    sheetLabel(sheet) {
      const key = `REAL_ESTATE.BULK_IMPORT.SHEET_${String(
        sheet
      ).toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : sheet;
    },
    async handleSubmit() {
      if (!this.file) {
        this.fileError = this.$t('REAL_ESTATE.BULK_IMPORT.VALIDATION_NO_FILES');
        return;
      }
      if (this.mode === 'replace' && !this.isConfirmingReplace) {
        this.isConfirmingReplace = true;
        return;
      }
      this.isConfirmingReplace = false;
      this.isSubmitting = true;
      this.fileError = '';
      this.invalidTemplate = [];
      try {
        const { data } = await startImport(this.file, this.mode);
        this.job = { job_id: data.job_id, status: data.status || 'queued' };
        this.startPolling(data.job_id);
      } catch (err) {
        if (apiErrorCode(err) === 'invalid_template') {
          this.invalidTemplate = this.templateProblems(err);
        }
        this.fileError = this.uploadErrorMessage(err);
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isSubmitting = false;
      }
    },
    startPolling(jobId) {
      this.stopPolling();
      this.poll(jobId);
      this.pollTimer = setInterval(() => this.poll(jobId), POLL_INTERVAL_MS);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async poll(jobId) {
      try {
        const { data } = await getImportJob(jobId);
        this.job = data;
        if (!ACTIVE_STATUSES.includes(data.status)) {
          this.stopPolling();
          this.$emit('imported');
        }
      } catch (err) {
        this.stopPolling();
        useAlert(apiErrorMessage(this, err));
        // eslint-disable-next-line no-console
        console.error(err);
      }
    },
    startAnother() {
      this.job = null;
      this.file = null;
      this.mode = 'merge';
      this.fileError = '';
      this.invalidTemplate = [];
    },
  },
};
</script>

<template>
  <div
    class="fixed inset-0 flex items-center justify-center bg-slate-800/80 z-50"
  >
    <div
      class="bg-white dark:bg-slate-900 rounded-lg p-6 shadow-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto"
    >
      <div class="flex items-center mb-4 justify-between">
        <h1 class="text-xl font-bold text-slate-900 dark:text-slate-100">
          {{ $t('REAL_ESTATE.BULK_IMPORT.MODAL_TITLE') }}
        </h1>
        <woot-button
          variant="clear"
          color-scheme="alert"
          icon="dismiss"
          @click="$emit('close')"
        />
      </div>

      <div
        class="flex gap-4 mb-4 border-b border-slate-200 dark:border-slate-700"
      >
        <button
          v-for="name in ['upload', 'history']"
          :key="name"
          type="button"
          class="-mb-px pb-2 text-sm font-medium border-b-2 transition-colors"
          :class="
            tab === name
              ? 'border-woot-500 text-woot-600 dark:text-woot-400'
              : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
          "
          @click="tab = name"
        >
          {{
            name === 'upload'
              ? $t('REAL_ESTATE.BULK_IMPORT.TAB_UPLOAD')
              : $t('REAL_ESTATE.BULK_IMPORT.TAB_HISTORY')
          }}
        </button>
      </div>

      <ImportHistory v-if="tab === 'history'" />

      <template v-else>
        <div v-if="!job" class="space-y-4">
          <div class="flex flex-wrap items-center justify-between gap-3">
            <p class="mb-0 text-sm text-slate-600 dark:text-slate-300">
              {{ $t('REAL_ESTATE.BULK_IMPORT.TEMPLATE_HINT') }}
            </p>
            <woot-button
              variant="smooth"
              size="small"
              icon="arrow-download"
              :is-loading="isDownloadingTemplate"
              @click="downloadTemplate"
            >
              {{ $t('REAL_ESTATE.BULK_IMPORT.DOWNLOAD_TEMPLATE') }}
            </woot-button>
          </div>

          <div>
            <label
              class="block mb-0 border-2 border-dashed rounded-lg p-6 text-center text-sm cursor-pointer hover:border-woot-500"
              :class="
                fileError
                  ? 'border-red-400 dark:border-red-500'
                  : 'border-slate-300 dark:border-slate-600'
              "
            >
              <span
                v-if="file"
                class="font-medium text-slate-900 dark:text-slate-100"
              >
                {{ file.name }}
              </span>
              <span v-else class="text-slate-500 dark:text-slate-400">
                {{ $t('REAL_ESTATE.BULK_IMPORT.UPLOAD_HINT') }}
              </span>
              <span
                v-if="file"
                class="block mt-1 text-xs text-slate-500 dark:text-slate-400"
              >
                {{ $t('REAL_ESTATE.BULK_IMPORT.CHANGE_FILE') }}
              </span>
              <input
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                class="hidden"
                @change="handleFileChange"
              />
            </label>
            <p v-if="fileError" class="mt-1 mb-0 text-xs text-red-500">
              {{ fileError }}
            </p>
            <div
              v-if="invalidTemplate.length"
              class="mt-2 rounded-md bg-red-50 dark:bg-red-900/30 px-3 py-2 text-xs text-red-700 dark:text-red-300"
            >
              <p class="mb-1 font-medium">
                {{ $t('REAL_ESTATE.BULK_IMPORT.INVALID_TEMPLATE_TITLE') }}
              </p>
              <ul class="mb-0 list-disc pl-4">
                <li v-for="line in invalidTemplate" :key="line">{{ line }}</li>
              </ul>
            </div>
          </div>

          <fieldset class="m-0 p-0 border-0">
            <legend
              class="mb-1 text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              {{ $t('REAL_ESTATE.BULK_IMPORT.MODE_LABEL') }}
            </legend>
            <div class="grid gap-2 sm:grid-cols-2">
              <label
                v-for="value in modes"
                :key="value"
                class="mb-0 flex gap-2 rounded-lg border p-3 cursor-pointer"
                :class="
                  mode === value
                    ? value === 'replace'
                      ? 'border-red-400 bg-red-50 dark:border-red-500 dark:bg-red-900/20'
                      : 'border-woot-500 bg-woot-50 dark:bg-woot-900/20'
                    : 'border-slate-200 dark:border-slate-700'
                "
              >
                <input
                  v-model="mode"
                  type="radio"
                  name="import-mode"
                  :value="value"
                  class="mt-0.5"
                />
                <span>
                  <span
                    class="block text-sm font-medium text-slate-900 dark:text-slate-100"
                  >
                    {{
                      $t(`REAL_ESTATE.BULK_IMPORT.MODE_${value.toUpperCase()}`)
                    }}
                  </span>
                  <span
                    class="block text-xs text-slate-500 dark:text-slate-400"
                  >
                    {{
                      $t(
                        `REAL_ESTATE.BULK_IMPORT.MODE_${value.toUpperCase()}_HINT`
                      )
                    }}
                  </span>
                </span>
              </label>
            </div>
          </fieldset>

          <div
            v-if="isConfirmingReplace"
            role="alert"
            class="rounded-md border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
          >
            <p class="mb-1 font-medium">
              {{ $t('REAL_ESTATE.BULK_IMPORT.REPLACE_CONFIRM_TITLE') }}
            </p>
            <p class="mb-0 text-xs">
              {{ $t('REAL_ESTATE.BULK_IMPORT.REPLACE_CONFIRM_BODY') }}
            </p>
          </div>

          <div class="rounded-lg border border-slate-200 dark:border-slate-700">
            <button
              type="button"
              class="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-200"
              :aria-expanded="isHelpOpen ? 'true' : 'false'"
              @click="isHelpOpen = !isHelpOpen"
            >
              {{ $t('REAL_ESTATE.BULK_IMPORT.HELP_TOGGLE') }}
              <fluent-icon
                :icon="isHelpOpen ? 'chevron-up' : 'chevron-down'"
                size="14"
              />
            </button>
            <ul
              v-if="isHelpOpen"
              class="mb-0 px-3 pb-3 pl-7 list-disc space-y-1 text-xs text-slate-600 dark:text-slate-300"
            >
              <li v-for="key in helpKeys" :key="key">
                {{ $t(`REAL_ESTATE.BULK_IMPORT.${key}`) }}
              </li>
            </ul>
          </div>
        </div>

        <div v-else-if="isRunning" class="py-8 text-center space-y-3">
          <p class="mb-0 text-sm text-slate-600 dark:text-slate-300">
            {{
              isQueued
                ? $t('REAL_ESTATE.BULK_IMPORT.QUEUED')
                : $t('REAL_ESTATE.BULK_IMPORT.PROCESSING_ROWS', {
                    processed: progress.processed_rows || 0,
                    total: progress.total_rows || 0,
                  })
            }}
          </p>
          <div
            class="w-full h-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden"
          >
            <div
              class="h-full bg-woot-500 transition-all"
              :style="{ width: `${isQueued ? 0 : progress.percentage || 0}%` }"
            />
          </div>
          <p class="mb-0 text-xs text-slate-500 dark:text-slate-400">
            {{ $t('REAL_ESTATE.BULK_IMPORT.CLOSE_WHILE_RUNNING') }}
          </p>
        </div>

        <ImportJobResults v-else-if="isDone" :job="job" />
      </template>

      <div
        class="flex justify-end gap-3 pt-4 mt-4 border-t border-slate-200 dark:border-slate-700"
      >
        <template v-if="tab === 'upload' && isDone">
          <woot-button variant="clear" @click="startAnother">
            {{ $t('REAL_ESTATE.BULK_IMPORT.START_ANOTHER') }}
          </woot-button>
          <woot-button @click="$emit('close')">
            {{ $t('REAL_ESTATE.BULK_IMPORT.DONE') }}
          </woot-button>
        </template>
        <template v-else-if="tab === 'upload' && isConfirmingReplace">
          <woot-button variant="clear" @click="isConfirmingReplace = false">
            {{ $t('REAL_ESTATE.BULK_IMPORT.REPLACE_CONFIRM_BACK') }}
          </woot-button>
          <woot-button
            color-scheme="alert"
            :is-loading="isSubmitting"
            @click="handleSubmit"
          >
            {{ $t('REAL_ESTATE.BULK_IMPORT.REPLACE_CONFIRM_START') }}
          </woot-button>
        </template>
        <template v-else-if="tab === 'upload' && !isRunning">
          <woot-button variant="clear" @click="$emit('close')">
            {{ $t('REAL_ESTATE.BULK_IMPORT.CLOSE') }}
          </woot-button>
          <woot-button :is-loading="isSubmitting" @click="handleSubmit">
            {{ $t('REAL_ESTATE.BULK_IMPORT.START_IMPORT') }}
          </woot-button>
        </template>
        <woot-button v-else variant="clear" @click="$emit('close')">
          {{ $t('REAL_ESTATE.BULK_IMPORT.CLOSE') }}
        </woot-button>
      </div>
    </div>
  </div>
</template>
